<template>
  <v-menu v-if="items.length" v-model="menuOpen" :close-on-content-click="false" location="bottom start" width="440">
    <template #activator="{ props }">
      <!-- Menu Activator -->
      <v-badge :content="activeCount" :model-value="activeCount > 0" color="info" offset-x="6" offset-y="6">
        <v-btn v-bind="props" variant="text" size="small" class="text-none app-progress-menu__activator">
          <v-icon start :icon="activatorIcon" />
          <span class="app-progress-menu__activator-label">{{ $t('AppProgressNotificationsTitle') }}</span>
          <v-icon end size="16">mdi-menu-down</v-icon>
        </v-btn>
      </v-badge>
    </template>

    <v-card flat>
      <v-card-title class="d-flex align-center ga-2">
        <span>{{ $t('AppProgressNotificationsTitle') }}</span>
        <v-spacer />
        <v-btn
          v-if="items.length"
          variant="text"
          size="small"
          prepend-icon="mdi-delete-sweep-outline"
          @click="clearProgress"
        >
          {{ $t('AppProgressNotificationsClear') }}
        </v-btn>
      </v-card-title>

      <v-divider />

      <v-card-text v-if="!items.length" class="text-medium-emphasis">
        {{ $t('AppProgressNotificationsEmpty') }}
      </v-card-text>

      <v-list v-else class="app-progress-menu__list">
        <!-- Notification Items -->
        <template v-for="(item, index) in items" :key="item.id">
          <v-list-item>
            <template #prepend>
              <v-icon :icon="stateIcon(item)" :color="stateColor(item)" />
            </template>

            <v-list-item-title class="app-progress-menu__title" :title="item.label">
              {{ item.label }}
            </v-list-item-title>

            <v-list-item-subtitle class="mt-2">
              <!-- Notification Summary -->
              <div class="app-progress-menu__summary">
                <span class="text-body-2">{{ stateLabel(item.state) }}</span>

                <div class="app-progress-menu__summary-progress">
                  <span>{{ progressLabel(item) }}</span>
                  <v-chip size="small" :color="stateColor(item)" variant="tonal"> {{ item.percent }}% </v-chip>
                </div>
              </div>

              <v-progress-linear class="mt-2" :model-value="item.percent" :color="stateColor(item)" rounded />

              <!-- Notification Step -->
              <div v-if="item.step" class="app-progress-menu__step mt-2 text-medium-emphasis">
                {{ item.step }}
              </div>

              <div class="app-progress-menu__meta mt-3 text-small">
                <span class="text-medium-emphasis">{{ $t('AppProgressNotificationsCreatedAt') }}</span>
                <RelativeDateTooltip :date="item.created_at" />

                <span class="text-medium-emphasis">{{ $t('AppProgressNotificationsCompletedAt') }}</span>
                <RelativeDateTooltip :date="item.completed_at" />

                <span class="text-medium-emphasis">{{ $t('AppProgressNotificationsUpdatedAt') }}</span>
                <RelativeDateTooltip :date="item.updated_at" />

                <span class="text-medium-emphasis">{{ $t('AppProgressNotificationsRuntime') }}</span>
                <span class="font-weight-medium">{{ runtimeLabel(item) }}</span>
              </div>
            </v-list-item-subtitle>
          </v-list-item>

          <v-divider v-if="index < items.length - 1" />
        </template>
      </v-list>
    </v-card>
  </v-menu>
</template>

<script lang="ts">
import { computed, defineComponent, watch } from 'vue'
import { useNow } from '@vueuse/core'
import { useProgressNotificationsMenu } from '~/composables/useProgressNotificationsMenu'
import {
  getProgressRuntimeLabel,
  getProgressStateColor,
  getProgressStateIcon,
  getProgressStateLabel,
  getProgressValueLabel,
  isCompletedProgress,
  Progress,
  useProgressNotifications,
} from '~/lib/notify'
import type { AppProgressNotificationRecord } from '~/lib/notify'
import RelativeDateTooltip from '~/components/Common/RelativeDateTooltip.vue'

export default defineComponent({
  name: 'AppProgressNotificationsMenu',
  components: {
    RelativeDateTooltip,
  },
  setup() {
    const items = useProgressNotifications()
    const { isOpen: menuOpen } = useProgressNotificationsMenu()
    const now = useNow({ interval: 1000 })

    const activeCount = computed(() => items.value.filter((item) => !isCompletedProgress(item)).length)
    const activatorIcon = computed(() => (activeCount.value > 0 ? 'mdi-progress-clock' : 'mdi-clipboard-text-clock'))

    function clearProgress() {
      Progress.clear()
      menuOpen.value = false
    }

    function stateColor(item: AppProgressNotificationRecord) {
      return getProgressStateColor(item)
    }

    function stateIcon(item: AppProgressNotificationRecord) {
      return getProgressStateIcon(item)
    }

    function progressLabel(item: AppProgressNotificationRecord) {
      return getProgressValueLabel(item)
    }

    function stateLabel(state: string) {
      return getProgressStateLabel(state)
    }

    function runtimeLabel(item: AppProgressNotificationRecord) {
      return getProgressRuntimeLabel(item, now.value)
    }

    watch(items, (progressItems) => {
      if (!progressItems.length) {
        menuOpen.value = false
      }
    })

    return {
      menuOpen,
      items,
      activeCount,
      activatorIcon,
      clearProgress,
      stateColor,
      stateIcon,
      progressLabel,
      stateLabel,
      runtimeLabel,
    }
  },
})
</script>

<style scoped lang="scss">
.app-progress-menu__activator {
  max-width: min(240px, calc(100vw - 160px));
}

.app-progress-menu__activator-label {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.app-progress-menu__list {
  max-height: min(60vh, 480px);
  overflow-y: auto;
}

.app-progress-menu__title {
  white-space: normal;
  overflow: visible;
  line-height: 1.4;
}

.app-progress-menu__summary {
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 8px 12px;
}

.app-progress-menu__summary-progress {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
  margin-left: auto;
}

.app-progress-menu__step {
  white-space: normal;
  line-height: 1.4;
}

.app-progress-menu__meta {
  display: grid;
  grid-template-columns: auto 1fr;
  gap: 0 12px;
  align-items: center;
}
</style>
